"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { Check, ChevronDown, Link2, Loader2, Unplug, AlertCircle, RefreshCw } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/utils";
import type { ConnectorInfo, ConnectionInfo, IntegrationsConnectConfig, ConnectorCategory } from "./types";
import { useIntegrations } from "./use-integrations";

const containerVariants = cva("rounded-lg border bg-card", {
  variants: {
    variant: {
      default: "border-border",
      minimal: "border-transparent shadow-sm",
    },
    size: {
      sm: "p-3",
      default: "p-4",
      lg: "p-6",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

const connectorItemVariants = cva(
  "flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer",
  {
    variants: {
      variant: {
        default: "border-border hover:border-primary/50 hover:bg-muted/50",
        connected: "border-green-500/50 bg-green-50 dark:bg-green-950/20",
        error: "border-destructive/50 bg-destructive/5",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface IntegrationsConnectProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">,
    VariantProps<typeof containerVariants>,
    IntegrationsConnectConfig {
  /**
   * Title for the component
   * @default "Connect your apps"
   */
  title?: string;
  /**
   * Description text
   */
  description?: string;
  /**
   * Show category filter tabs
   * @default false
   */
  showCategoryFilter?: boolean;
  /**
   * Show connected integrations count
   * @default true
   */
  showConnectionCount?: boolean;
  /**
   * Whether to group connectors by category
   * @default false
   */
  groupByCategory?: boolean;
  /**
   * Custom empty state content
   */
  emptyState?: React.ReactNode;
}

const CATEGORY_LABELS: Record<ConnectorCategory, string> = {
  crm: "CRM",
  storage: "Storage",
  communication: "Communication",
  productivity: "Productivity",
};

const CATEGORY_ORDER: ConnectorCategory[] = ["crm", "storage", "communication", "productivity"];

export function IntegrationsConnect({
  apiKey,
  baseUrl,
  category: initialCategory,
  connectors: connectorFilter,
  onConnected,
  onConnectionError,
  onDisconnected,
  title = "Connect your apps",
  description,
  showCategoryFilter = false,
  showConnectionCount = true,
  groupByCategory = false,
  emptyState,
  className,
  variant,
  size,
  ...props
}: IntegrationsConnectProps) {
  const [selectedCategory, setSelectedCategory] = React.useState<ConnectorCategory | undefined>(initialCategory);
  const [connectingSlug, setConnectingSlug] = React.useState<string | null>(null);

  const { connectors, connections, isLoading, error, connect, disconnect, refresh } = useIntegrations({
    apiKey,
    baseUrl,
    category: selectedCategory || initialCategory,
    connectors: connectorFilter,
    onConnected,
    onConnectionError,
    onDisconnected,
  });

  const handleConnect = async (connector: ConnectorInfo) => {
    setConnectingSlug(connector.slug);
    try {
      await connect(connector.slug);
    } finally {
      setConnectingSlug(null);
    }
  };

  const handleDisconnect = async (connection: ConnectionInfo) => {
    setConnectingSlug(connection.connectorSlug);
    try {
      await disconnect(connection.id);
    } finally {
      setConnectingSlug(null);
    }
  };

  // Get connection for a connector
  const getConnectionForConnector = (connectorSlug: string): ConnectionInfo | undefined => {
    return connections.find((c) => c.connectorSlug === connectorSlug && c.status === "connected");
  };

  // Group connectors by category if needed
  const groupedConnectors = React.useMemo(() => {
    if (!groupByCategory) return { all: connectors };

    const groups: Record<string, ConnectorInfo[]> = {};
    for (const connector of connectors) {
      const cat = connector.category;
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(connector);
    }
    return groups;
  }, [connectors, groupByCategory]);

  // Filter categories that have connectors
  const availableCategories = React.useMemo(() => {
    const categories = new Set(connectors.map((c) => c.category));
    return CATEGORY_ORDER.filter((cat) => categories.has(cat));
  }, [connectors]);

  const connectedCount = connections.filter((c) => c.status === "connected").length;

  return (
    <div className={cn(containerVariants({ variant, size }), className)} {...props}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
          {showConnectionCount && connectedCount > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              {connectedCount} integration{connectedCount !== 1 ? "s" : ""} connected
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={isLoading}
          className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
        </button>
      </div>

      {/* Category filter */}
      {showCategoryFilter && availableCategories.length > 1 && (
        <div className="flex gap-2 mb-4 flex-wrap">
          <button
            type="button"
            onClick={() => setSelectedCategory(undefined)}
            className={cn(
              "px-3 py-1.5 text-sm rounded-full transition-colors",
              !selectedCategory
                ? "bg-primary text-primary-foreground"
                : "bg-muted hover:bg-muted/80 text-muted-foreground",
            )}
          >
            All
          </button>
          {availableCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={cn(
                "px-3 py-1.5 text-sm rounded-full transition-colors",
                selectedCategory === cat
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted hover:bg-muted/80 text-muted-foreground",
              )}
            >
              {CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive mb-4">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Loading state */}
      {isLoading && connectors.length === 0 && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && connectors.length === 0 && (
        emptyState || (
          <div className="text-center py-8 text-muted-foreground">
            <Link2 className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No integrations available</p>
          </div>
        )
      )}

      {/* Connector list */}
      {connectors.length > 0 && (
        <div className="space-y-4">
          {groupByCategory ? (
            // Grouped by category
            Object.entries(groupedConnectors).map(([cat, catConnectors]) => (
              <div key={cat}>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">
                  {CATEGORY_LABELS[cat as ConnectorCategory]}
                </h4>
                <div className="space-y-2">
                  {catConnectors.map((connector) => (
                    <ConnectorItem
                      key={connector.slug}
                      connector={connector}
                      connection={getConnectionForConnector(connector.slug)}
                      isConnecting={connectingSlug === connector.slug}
                      onConnect={() => handleConnect(connector)}
                      onDisconnect={(conn) => handleDisconnect(conn)}
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            // Flat list
            <div className="space-y-2">
              {connectors.map((connector) => (
                <ConnectorItem
                  key={connector.slug}
                  connector={connector}
                  connection={getConnectionForConnector(connector.slug)}
                  isConnecting={connectingSlug === connector.slug}
                  onConnect={() => handleConnect(connector)}
                  onDisconnect={(conn) => handleDisconnect(conn)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ConnectorItemProps {
  connector: ConnectorInfo;
  connection?: ConnectionInfo;
  isConnecting: boolean;
  onConnect: () => void;
  onDisconnect: (connection: ConnectionInfo) => void;
}

function ConnectorItem({ connector, connection, isConnecting, onConnect, onDisconnect }: ConnectorItemProps) {
  const [showMenu, setShowMenu] = React.useState(false);
  const isConnected = !!connection;

  return (
    <div
      className={cn(
        connectorItemVariants({
          variant: isConnected ? "connected" : "default",
        }),
      )}
    >
      {/* Icon */}
      <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
        {connector.iconUrl ? (
          <img src={connector.iconUrl} alt={connector.name} className="h-6 w-6 object-contain" />
        ) : (
          <Link2 className="h-5 w-5 text-muted-foreground" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium">{connector.name}</span>
          {isConnected && <Check className="h-4 w-4 text-green-500" />}
        </div>
        {connector.description && (
          <p className="text-sm text-muted-foreground truncate">{connector.description}</p>
        )}
        {isConnected && connection?.externalAccountName && (
          <p className="text-xs text-muted-foreground">Connected as {connection.externalAccountName}</p>
        )}
      </div>

      {/* Action */}
      <div className="flex-shrink-0 relative">
        {isConnecting ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : isConnected ? (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowMenu(!showMenu)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-muted hover:bg-muted/80 transition-colors"
            >
              Connected
              <ChevronDown className="h-4 w-4" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-20 bg-popover border rounded-md shadow-lg py-1 min-w-[140px]">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMenu(false);
                      if (connection) onDisconnect(connection);
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors text-destructive"
                  >
                    <Unplug className="h-4 w-4" />
                    Disconnect
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={onConnect}
            className="px-4 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Connect
          </button>
        )}
      </div>
    </div>
  );
}

IntegrationsConnect.displayName = "IntegrationsConnect";
